import { combineReducers, createListenerMiddleware } from '@reduxjs/toolkit'
import persistReducer from 'redux-persist/es/persistReducer'
import storage from './swIndexedDBStorage'; // ← our adapter
import { elementsReducer, ElementsState } from './slices/elements/reducer'
import { uploadsReducer } from './slices/uploads/reducer'
import { PersistConfig } from 'redux-persist/es/types'
import { fontsReducer } from './slices/fonts/reducer'
import { templatesReducer } from './slices/templates/reducer'
import { creationsReducer } from './slices/creations/reducer'
import { backgroundPacksReducer } from './slices/backgroundPacks/reducer'
import { packsReducer } from './slices/packs/reducer'
import { graphicPacksReducer } from './slices/graphicsPacks/reducer'
import { effectsReducer } from './slices/effect/reducer'
import { imageElementsReducer } from './slices/imageElement/reducer'
import { userReducer } from './slices/user/reducer'
import { editorReducer } from './slices/editor/reducer'
import { removeToolReducer } from './slices/removeTool/reducer'
import { magicBgToolReducer } from './slices/magicBgTool/reducer'
import draftsReducer, { DraftsState, resetAllIsAcquiringData } from './slices/drafts/reducer'
import { aiImageReducer } from './slices/aiImage/reducer'
import { aiFiltersReducer } from './slices/aiFilter/reducer'
import { aiVideoReducer } from './slices/aiVideo/reducer'
import { homeFeedReducer } from './slices/homeFeed/reducer'
import deeplinkReducer from './slices/deeplink/reducer'
import { aiFashionModelReducer } from './slices/aiFashionModels/reducer'
import { aiFashionReducer } from './slices/aiFashion/slice'
import { modalReducer } from './slices/modal/reducer'
import { photoshootReducer } from './slices/photoshoot/reducer'
import { batchReducer } from './slices/batch/slice'
import { MediaImageRepositoryProvider } from '@/scenes/engine/objects/media-repository/MediaImageRepositoryProvider';
import {
  OPEN_IN_EDITOR_SOURCE_PROJECT_QUERY_PARAM,
  OPEN_IN_EDITOR_SOURCE_PROJECT_SESSION_KEY,
} from '@/utils/indexedImage'

const DRAFTS_PERSIST_KEY = 'drafts';
const REHYDRATE_TYPE = 'persist/REHYDRATE' as const;

function getDraftProjectIdsToPreserve(state: { drafts?: DraftsState }): string[] {
  const projectIds = state.drafts?.projects
    ?.filter((draft) => draft.isDraftMode || draft.isAcquiringData)
    .map((draft) => draft.projectId)
    .filter((projectId): projectId is string => Boolean(projectId)) ?? [];

  const openInEditorSourceProjectId = getOpenInEditorSourceProjectId()
  if (openInEditorSourceProjectId) {
    projectIds.push(openInEditorSourceProjectId)
  }

  return Array.from(new Set(projectIds));
}

function getOpenInEditorSourceProjectId(): string | null {
  if (typeof window === 'undefined') return null

  const querySourceProjectId = new URLSearchParams(window.location.search)
    .get(OPEN_IN_EDITOR_SOURCE_PROJECT_QUERY_PARAM)

  if (querySourceProjectId) {
    window.sessionStorage.setItem(OPEN_IN_EDITOR_SOURCE_PROJECT_SESSION_KEY, querySourceProjectId)
    return querySourceProjectId
  }

  return window.sessionStorage.getItem(OPEN_IN_EDITOR_SOURCE_PROJECT_SESSION_KEY)
}

// import { authReducer } from "@store/slices/auth/reducer"
// import { AuthState } from "./slices/auth/reducer"
// import { occasionsReducer } from "./slices/occasions/reducer"
// import { detailsReducer, DetailsState } from "./slices/details/reducer"
// import { feedbackReducer } from "./slices/feedback/reducer"

// const authPersistConfig: PersistConfig<AuthState> = {
//   key: "auth",
//   storage,
//   blacklist: ["errors", "passwordChageStatus"],
// }


// ---- listener middleware
export const draftsListenerMiddleware = createListenerMiddleware();

// Fires when the drafts slice rehydrates.
// If you accidentally change the persist key, update DRAFTS_PERSIST_KEY above.
draftsListenerMiddleware.startListening({
  predicate: (action): boolean => {
    // Guard: only react to persist/REHYDRATE
    if (action.type !== REHYDRATE_TYPE) return false;

    // Some apps persist the *root* (key === 'root') or a different key.
    // We only want the rehydrate for our drafts slice (the persist key we used).
    return (action as any).key === DRAFTS_PERSIST_KEY;
  },
  effect: async (action, api) => {
    // optional: prove it's running
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.info('[rehydrate]', (action as any).key);
    }
    api.dispatch(resetAllIsAcquiringData());

    const draftsState = api.getState() as { drafts?: DraftsState };
    const draftIdsToPreserve = getDraftProjectIdsToPreserve(draftsState);

    MediaImageRepositoryProvider.getEditingProjectInstance();
    await MediaImageRepositoryProvider.clearEditingProjectInstance(draftIdsToPreserve);
  },
});


const elementsPersistConfig: PersistConfig<ElementsState> = {
  key: 'elements',
  storage,
  whitelist: ['elements'],
  timeout: 10000,          // 10s (or whatever you need)
}

const draftsPersistConfig: PersistConfig<DraftsState> = {
  key: DRAFTS_PERSIST_KEY,
  storage,
}

const rootReducer = combineReducers({
  editor: combineReducers({
    elements: persistReducer(elementsPersistConfig, elementsReducer),
    uploads: uploadsReducer,
    fonts: fontsReducer,
    backgroundPacks: backgroundPacksReducer,
    templates: templatesReducer,
    packs: packsReducer,
    graphicPacks: graphicPacksReducer,
    effects: effectsReducer,
    imageElements: imageElementsReducer,
    user: userReducer,
    editor: editorReducer,
    removeTool: removeToolReducer,
    magicBgToolReducer: magicBgToolReducer,
    aiImageReducer: aiImageReducer,
    aiFilterReducer: aiFiltersReducer,
    aiVideo: aiVideoReducer,
    homeFeed: homeFeedReducer,
    aiFashionModelReducer: aiFashionModelReducer,
    aiFashionReducer: aiFashionReducer,
  }),
  creations: creationsReducer,
  deeplink: deeplinkReducer,
  drafts: persistReducer(draftsPersistConfig, draftsReducer),
  modal: modalReducer,
  photoshoot: photoshootReducer,
  batch: batchReducer,
  // auth: persistReducer(authPersistConfig, authReducer),
  // occassions: occasionsReducer,
  // details: persistReducer(detailsPersistConfig, detailsReducer),
  // feedback: feedbackReducer,
})

export type RootState = ReturnType<typeof rootReducer>

export default rootReducer
